import React, { createContext, useContext, useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { onAuthStateChange, handleRedirectResult } from '../utils/firebase';
import { doc, onSnapshot, collection, query, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db } from '../utils/firebase/config';

interface Subscription {
  status: 'active' | 'canceled' | 'past_due' | 'unpaid' | 'incomplete' | 'incomplete_expired' | 'trialing';
  current_period_end: Timestamp;
  product: {
    name: string;
    features: string[];
  };
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  subscription: Subscription | null;
  hasPremiumAccess: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  subscription: null,
  hasPremiumAccess: false
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let subscriptionUnsubscribe: (() => void) | undefined;

    const unsubscribe = onAuthStateChange(async (authUser) => {
      setUser(authUser);

      if (authUser) {
        // Subscribe to user's subscription status
        const subscriptionsRef = collection(db, 'customers', authUser.uid, 'subscriptions');
        const q = query(subscriptionsRef, orderBy('created', 'desc'), limit(1));
        
        subscriptionUnsubscribe = onSnapshot(q, (snapshot) => {
          if (!snapshot.empty) {
            const subData = snapshot.docs[0].data() as Subscription;
            setSubscription(subData);
          } else {
            setSubscription(null);
            if (authUser.isAnonymous) {
              setUser(null);
            }
          }
          setLoading(false);
        });
      } else {
        setSubscription(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
      if (subscriptionUnsubscribe) {
        subscriptionUnsubscribe();
      }
    };
  }, []);

  const hasPremiumAccess =
    subscription?.status === 'active' || subscription?.status === 'trialing';

  if (!loading && user?.isAnonymous && !hasPremiumAccess) {
    setUser(null);
    setSubscription(null);
  }

  return (
    <AuthContext.Provider value={{ user, loading, subscription, hasPremiumAccess }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};